import axios from "axios";

import type {
  OnboardingProgress,
  OnboardingWelcomeChoice,
  TourId,
} from "../app/slices/OnboardingSlice";

const BASE_URL = process.env.REACT_APP_PLAYIT_CREATORAPI;

export interface UserSettings {
  darkMode: boolean;
  autoSave: boolean;
  onboarding: OnboardingProgress | null;
}

interface AbandonedOnboardingTourPayload {
  tourId: string;
  stepIndex: number;
}

interface OnboardingSettingsPayload {
  completedTours: string[];
  abandonedTours: AbandonedOnboardingTourPayload[];
  dismissedHints: string[];
  welcomeChoice: OnboardingWelcomeChoice;
}

interface UserSettingsPayload {
  darkMode: boolean;
  autoSave: boolean;
  onboarding?: OnboardingSettingsPayload | null;
}

export type UserSettingsLoadResult =
  | { status: "loaded"; settings: UserSettings | null }
  | { status: "failed" };

export const DEFAULT_USER_SETTINGS: UserSettings = {
  darkMode: false,
  autoSave: true,
  onboarding: null,
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isWelcomeChoice = (
  value: unknown
): value is OnboardingWelcomeChoice =>
  value === "unseen" || value === "started" || value === "dismissed";

const isAbandonedTourPayload = (
  value: unknown
): value is AbandonedOnboardingTourPayload =>
  isObject(value) &&
  typeof value.tourId === "string" &&
  value.tourId.length > 0 &&
  typeof value.stepIndex === "number" &&
  Number.isInteger(value.stepIndex) &&
  value.stepIndex >= 0;

const isOnboardingSettingsPayload = (
  value: unknown
): value is OnboardingSettingsPayload =>
  isObject(value) &&
  isStringArray(value.completedTours) &&
  Array.isArray(value.abandonedTours) &&
  value.abandonedTours.every(isAbandonedTourPayload) &&
  isStringArray(value.dismissedHints) &&
  isWelcomeChoice(value.welcomeChoice);

const isUserSettingsPayload = (value: unknown): value is UserSettingsPayload =>
  isObject(value) &&
  typeof value.darkMode === "boolean" &&
  typeof value.autoSave === "boolean" &&
  (value.onboarding === undefined ||
    value.onboarding === null ||
    isOnboardingSettingsPayload(value.onboarding));

const fromOnboardingPayload = (
  onboarding: OnboardingSettingsPayload | null | undefined
): OnboardingProgress | null => {
  if (!onboarding) return null;

  const abandonedTours: OnboardingProgress["abandonedTours"] = {};
  onboarding.abandonedTours.forEach(({ tourId, stepIndex }) => {
    abandonedTours[tourId as TourId] = stepIndex;
  });

  return {
    completedTours: onboarding.completedTours as TourId[],
    abandonedTours,
    dismissedHints: onboarding.dismissedHints,
    welcomeChoice: onboarding.welcomeChoice,
  };
};

const fromUserSettingsPayload = (
  settings: UserSettingsPayload
): UserSettings => ({
  darkMode: settings.darkMode,
  autoSave: settings.autoSave,
  onboarding: fromOnboardingPayload(settings.onboarding),
});

const toOnboardingPayload = (
  onboarding: OnboardingProgress | null
): OnboardingSettingsPayload | null => {
  if (!onboarding) return null;

  return {
    completedTours: onboarding.completedTours,
    abandonedTours: Object.entries(onboarding.abandonedTours).flatMap(
      ([tourId, stepIndex]) =>
        typeof stepIndex === "number" ? [{ tourId, stepIndex }] : []
    ),
    dismissedHints: onboarding.dismissedHints,
    welcomeChoice: onboarding.welcomeChoice,
  };
};

const toUserSettingsPayload = (settings: UserSettings): UserSettingsPayload => ({
  darkMode: settings.darkMode,
  autoSave: settings.autoSave,
  onboarding: toOnboardingPayload(settings.onboarding),
});

const getConfig = (token: string) => ({
  headers: {
    Authorization: "Bearer " + token,
  },
});

const getJsonConfig = (token: string) => ({
  headers: {
    Authorization: "Bearer " + token,
    "Content-Type": "application/json",
  },
});

export class UserSettingsService {
  static async GetUserSettings(token: string): Promise<UserSettingsLoadResult> {
    try {
      const response = await axios.get<UserSettingsPayload>(
        BASE_URL + "/user-settings",
        getConfig(token)
      );

      if (response.status === 204) {
        return { status: "loaded", settings: null };
      }

      if (!isUserSettingsPayload(response.data)) {
        return { status: "failed" };
      }

      return {
        status: "loaded",
        settings: fromUserSettingsPayload(response.data),
      };
    } catch {
      return { status: "failed" };
    }
  }

  static async PutUserSettings(
    token: string,
    settings: UserSettings
  ): Promise<boolean> {
    try {
      const response = await axios.put(
        BASE_URL + "/user-settings",
        toUserSettingsPayload(settings),
        getJsonConfig(token)
      );

      return response.status === 204;
    } catch {
      return false;
    }
  }
}
